import React, { useMemo, useState } from "react";

const MIN = (h, m) => h * 60 + m;

const SHIFT = {
    windowStart: MIN(9, 0),     // 09:00
    windowEnd: MIN(19, 30),     // 19:30
    fullDayHours: 8.5,          // 8.5 hrs
    firstHalfEnd: MIN(13, 0),   // 13:00 (1st half ends)
    secondHalfStart: MIN(14, 0),// 14:00 (2nd half starts)
    secondHalfEnd: MIN(18, 30), // 18:30 (2nd half ends)
    lateHalfDayAfter: MIN(11, 0), // punch-in post 11 => half-day
    absentAfter: MIN(14, 0),      // punch-in after 2pm => absent
};

function parseTimeToMinutes(value) {
    if (!value || !/^\d{2}:\d{2}$/.test(value)) return null;
    const [hh, mm] = value.split(":").map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
}

function minutesToHours(mins) {
    return mins / 60;
}

function formatHours(hours) {
    return (Math.round(hours * 100) / 100).toFixed(2);
}

/**
 * Clamp punch times:
 * - if punch-in before 09:00 => count from 09:00
 * - if punch-out after 19:30 => count till 19:30
 */
function clampToWindow(punchInMin, punchOutMin) {
    const adjIn = Math.max(punchInMin, SHIFT.windowStart);
    const adjOut = Math.min(punchOutMin, SHIFT.windowEnd);
    return { adjIn, adjOut };
}

/**
 * Apply your status rules using adjusted/clamped times for working hour calculation.
 * IMPORTANT:
 * - "punch-in after 2pm => absent" should be based on ORIGINAL first punch-in time (not clamped)
 *   because someone might punch at 08:30, clamp to 09:00, still OK.
 */
function evaluateStatus(originalIn, originalOut, adjIn, adjOut) {
    // Validate window overlap after clamping
    if (adjOut <= adjIn) {
        return {
            status: "Absent",
            reason:
                "No valid working time inside office window (09:00–19:30) after applying rules.",
            workedHours: 0,
        };
    }

    const workedMinutes = adjOut - adjIn;
    const workedHours = minutesToHours(workedMinutes);

    // RULE: after 2pm punch => absent (strictly > 14:00) based on ORIGINAL punch-in
    if (originalIn > SHIFT.absentAfter) {
        return {
            status: "Absent",
            reason: "Punch-in after 2:00 PM is considered Absent.",
            workedHours,
        };
    }

    // RULE: < 4 hrs => full day leave (Absent)
    if (workedHours < 4) {
        return {
            status: "Absent",
            reason: "Worked less than 4 hours (within the window), considered full day leave.",
            workedHours,
        };
    }

    // RULE: punch-in post 11 AM => Half-day (based on ORIGINAL punch-in)
    // For late punch-in, ensure at least 4.5 hours for 2nd-half style
    if (originalIn > SHIFT.lateHalfDayAfter) {
        if (workedHours >= 4.5) {
            return {
                status: "Half Day",
                halfType: "2nd Half (late punch-in)",
                reason: "Punch-in after 11:00 AM is considered Half Day.",
                workedHours,
            };
        }
        return {
            status: "Absent",
            reason:
                "Punch-in after 11:00 AM needs at least 4.5 hours (within window) to be counted as Half Day; otherwise Absent.",
            workedHours,
        };
    }

    // Full day
    if (workedHours >= SHIFT.fullDayHours) {
        return {
            status: "Full Day Present",
            reason: `Worked ${formatHours(workedHours)} hrs (>= 8.5 hrs).`,
            workedHours,
        };
    }

    // Half day logic (>= 4 but < 8.5)
    // Determine half type based on adjusted times (because counting uses adjusted)
    let halfType = "Half Day";
    if (adjOut <= SHIFT.firstHalfEnd) halfType = "1st Half (morning)";
    else if (adjIn >= SHIFT.secondHalfStart) halfType = "2nd Half (evening)";

    // If it is 2nd half, require 4.5
    if (halfType.startsWith("2nd Half") && workedHours < 4.5) {
        return {
            status: "Absent",
            reason: "2nd Half requires at least 4.5 hours (within window); otherwise Absent.",
            workedHours,
        };
    }

    return {
        status: "Half Day",
        halfType,
        reason: `Worked ${formatHours(workedHours)} hrs (< 8.5 hrs).`,
        workedHours,
    };
}

export default function AttendancePage() {
    // Multi-punch list
    const [punches, setPunches] = useState([{ type: "IN", time: "" }, { type: "OUT", time: "" }]);
    const [result, setResult] = useState(null);

    const computed = useMemo(() => {
        // collect valid times
        const parsed = punches
            .map((p, idx) => ({ ...p, idx, min: parseTimeToMinutes(p.time) }))
            .filter((p) => p.min != null);

        // choose first IN and last OUT
        const ins = parsed.filter((p) => p.type === "IN").sort((a, b) => a.min - b.min);
        const outs = parsed.filter((p) => p.type === "OUT").sort((a, b) => a.min - b.min);

        const firstIn = ins.length ? ins[0].min : null;
        const lastOut = outs.length ? outs[outs.length - 1].min : null;

        if (firstIn == null || lastOut == null) {
            return { firstIn, lastOut, adjIn: null, adjOut: null };
        }

        const { adjIn, adjOut } = clampToWindow(firstIn, lastOut);
        return { firstIn, lastOut, adjIn, adjOut };
    }, [punches]);

    const addPunch = (type) => {
        setPunches((prev) => [...prev, { type, time: "" }]);
    };

    const updatePunch = (index, key, value) => {
        setPunches((prev) => prev.map((p, i) => (i === index ? { ...p, [key]: value } : p)));
    };

    const removePunch = (index) => {
        setPunches((prev) => prev.filter((_, i) => i !== index));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const { firstIn, lastOut, adjIn, adjOut } = computed;

        if (firstIn == null || lastOut == null) {
            setResult({
                status: "",
                reason: "Please add at least one Punch-IN and one Punch-OUT time.",
            });
            return;
        }

        if (lastOut <= firstIn) {
            setResult({
                status: "",
                reason: "Last Punch-OUT must be after first Punch-IN.",
            });
            return;
        }

        const evalRes = evaluateStatus(firstIn, lastOut, adjIn, adjOut);

        setResult({
            ...evalRes,
            originalIn: firstIn,
            originalOut: lastOut,
            adjIn,
            adjOut,
        });
    };

    const badgeStyle = (status) => {
        const base = {
            padding: "8px 12px",
            borderRadius: 999,
            fontWeight: 800,
            display: "inline-block",
            fontSize: 14,
        };
        if (status === "Full Day Present")
            return { ...base, background: "#E8FFF2", color: "#0E7A3A", border: "1px solid #7BE3A6" };
        if (status === "Half Day")
            return { ...base, background: "#FFF7E6", color: "#8A5A00", border: "1px solid #FFD18A" };
        if (status === "Absent")
            return { ...base, background: "#FFECEC", color: "#B42318", border: "1px solid #FFB3B3" };
        return { ...base, background: "#F2F4F7", color: "#344054", border: "1px solid #EAECF0" };
    };

    const toHHMM = (min) => {
        if (min == null) return "—";
        const hh = String(Math.floor(min / 60)).padStart(2, "0");
        const mm = String(min % 60).padStart(2, "0");
        return `${hh}:${mm}`;
    };

    return (
        <div style={{ maxWidth: 820, margin: "30px auto", padding: 16 }}>
            <h2 style={{ marginBottom: 6 }}>Attendance Status (Multi Punch)</h2>
            <p style={{ marginTop: 0, color: "#475467" }}>
                Window: <b>09:00–19:30</b> • Full day: <b>8.5 hrs</b> • We take <b>first IN</b> + <b>last OUT</b>
            </p>

            <form
                onSubmit={onSubmit}
                style={{
                    background: "#fff",
                    border: "1px solid #EAECF0",
                    borderRadius: 12,
                    padding: 16,
                    boxShadow: "0 6px 18px rgba(16,24,40,0.06)",
                }}
            >
                <div style={{ display: "grid", gap: 10 }}>
                    {punches.map((p, idx) => (
                        <div
                            key={idx}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "110px 1fr 90px",
                                gap: 10,
                                alignItems: "center",
                            }}
                        >
                            <select
                                value={p.type}
                                onChange={(e) => updatePunch(idx, "type", e.target.value)}
                                style={{
                                    padding: 10,
                                    borderRadius: 10,
                                    border: "1px solid #D0D5DD",
                                    fontWeight: 700,
                                }}
                            >
                                <option value="IN">IN</option>
                                <option value="OUT">OUT</option>
                            </select>

                            <input
                                type="time"
                                value={p.time}
                                onChange={(e) => updatePunch(idx, "time", e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: 10,
                                    borderRadius: 10,
                                    border: "1px solid #D0D5DD",
                                }}
                            />

                            <button
                                type="button"
                                onClick={() => removePunch(idx)}
                                disabled={punches.length <= 2}
                                style={{
                                    padding: "10px 12px",
                                    borderRadius: 10,
                                    border: "1px solid #D0D5DD",
                                    background: punches.length <= 2 ? "#F2F4F7" : "#fff",
                                    cursor: punches.length <= 2 ? "not-allowed" : "pointer",
                                    fontWeight: 800,
                                }}
                                title={punches.length <= 2 ? "Keep at least 2 rows" : "Remove"}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                        type="button"
                        onClick={() => addPunch("IN")}
                        style={{
                            padding: "10px 12px",
                            borderRadius: 10,
                            border: "1px solid #D0D5DD",
                            background: "#fff",
                            fontWeight: 800,
                            cursor: "pointer",
                        }}
                    >
                        + Add Punch IN
                    </button>

                    <button
                        type="button"
                        onClick={() => addPunch("OUT")}
                        style={{
                            padding: "10px 12px",
                            borderRadius: 10,
                            border: "1px solid #D0D5DD",
                            background: "#fff",
                            fontWeight: 800,
                            cursor: "pointer",
                        }}
                    >
                        + Add Punch OUT
                    </button>

                    <div style={{ flex: 1 }} />

                    <button
                        type="submit"
                        style={{
                            padding: "10px 14px",
                            borderRadius: 10,
                            border: "none",
                            background: "#111827",
                            color: "#fff",
                            fontWeight: 900,
                            cursor: "pointer",
                        }}
                    >
                        Check Status
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setPunches([{ type: "IN", time: "" }, { type: "OUT", time: "" }]);
                            setResult(null);
                        }}
                        style={{
                            padding: "10px 14px",
                            borderRadius: 10,
                            border: "1px solid #D0D5DD",
                            background: "#fff",
                            fontWeight: 900,
                            cursor: "pointer",
                        }}
                    >
                        Reset
                    </button>
                </div>

                <div style={{ marginTop: 10, color: "#667085", fontSize: 13, lineHeight: 1.5 }}>
                    <b>Extra conditions implemented:</b><br />
                    • If IN is before 09:00 ⇒ count starts at 09:00<br />
                    • If OUT is after 19:30 ⇒ count ends at 19:30<br />
                    • Multiple punches ⇒ we take first IN + last OUT
                </div>
            </form>

            {result && (
                <div
                    style={{
                        marginTop: 16,
                        background: "#fff",
                        border: "1px solid #EAECF0",
                        borderRadius: 12,
                        padding: 16,
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div>
                            <div style={{ fontWeight: 900, marginBottom: 6 }}>Result</div>
                            <span style={badgeStyle(result.status)}>{result.status || "—"}</span>
                            {result.halfType && (
                                <div style={{ marginTop: 8, color: "#475467", fontWeight: 700 }}>
                                    {result.halfType}
                                </div>
                            )}
                        </div>

                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 900, marginBottom: 6 }}>Worked Hours (Window Counted)</div>
                            <div style={{ fontSize: 22, fontWeight: 900 }}>
                                {typeof result.workedHours === "number" ? formatHours(result.workedHours) : "—"} hrs
                            </div>
                        </div>
                    </div>

                    <hr style={{ margin: "14px 0", border: "none", borderTop: "1px solid #EAECF0" }} />

                    <div style={{ fontWeight: 700, color: "#344054" }}>{result.reason}</div>

                    <div style={{ marginTop: 10, color: "#667085", fontSize: 13, lineHeight: 1.5 }}>
                        <b>First IN / Last OUT used:</b> {toHHMM(result.originalIn)} → {toHHMM(result.originalOut)} <br />
                        <b>Counted (clamped) time:</b> {toHHMM(result.adjIn)} → {toHHMM(result.adjOut)} <br />
                        <b>Note:</b> Late rules (after 11 AM / after 2 PM) are checked using the <b>original first punch-in</b>.
                    </div>
                </div>
            )}
        </div>
    );
}



// import React, { useMemo, useState } from "react";

// const MIN = (h, m) => h * 60 + m;

// const SHIFT = {
//     windowStart: MIN(9, 0),     // 09:00
//     windowEnd: MIN(19, 30),     // 19:30
//     fullDayHours: 8.5,          // 8.5 hrs
//     firstHalfStart: MIN(9, 0),  // 09:00
//     firstHalfEnd: MIN(13, 0),   // 13:00 (4 hrs)
//     secondHalfStart: MIN(14, 0),// 14:00
//     secondHalfEnd: MIN(18, 30), // 18:30 (4.5 hrs)
//     lateHalfDayAfter: MIN(11, 0), // Punch-in post 11:00 => Half-day
//     absentAfter: MIN(14, 0),      // After 2pm punch => absent (strictly > 14:00)
// };

// function parseTimeToMinutes(value) {
//     // value like "09:30"
//     if (!value || !/^\d{2}:\d{2}$/.test(value)) return null;
//     const [hh, mm] = value.split(":").map(Number);
//     if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
//     if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
//     return hh * 60 + mm;
// }

// function minutesToHours(mins) {
//     return mins / 60;
// }

// function formatHours(hours) {
//     // show 2 decimals without rounding weirdness
//     return (Math.round(hours * 100) / 100).toFixed(2);
// }

// function getStatus(punchInMin, punchOutMin) {
//     // basic validations
//     if (punchInMin == null || punchOutMin == null) {
//         return { status: "", reason: "Please select both Punch-in and Punch-out." };
//     }
//     if (punchOutMin <= punchInMin) {
//         return { status: "", reason: "Punch-out must be after Punch-in." };
//     }
//     if (punchInMin < SHIFT.windowStart || punchOutMin > SHIFT.windowEnd) {
//         return {
//             status: "",
//             reason: `Time must be within office window (09:00 to 19:30).`,
//         };
//     }

//     const workedMinutes = punchOutMin - punchInMin;
//     const workedHours = minutesToHours(workedMinutes);

//     // RULE: after 2pm punch (strictly after 14:00) => Absent
//     if (punchInMin > SHIFT.absentAfter) {
//         return {
//             status: "Absent",
//             reason: "Punch-in after 2:00 PM is considered Absent.",
//             workedHours,
//         };
//     }

//     // RULE: working hour < 4 => full day leave (Absent)
//     if (workedHours < 4) {
//         return {
//             status: "Absent",
//             reason: "Worked less than 4 hours, considered full day leave.",
//             workedHours,
//         };
//     }

//     // RULE: Punch-in post 11 AM => Half-day
//     // We'll require at least 4.5 hrs to count it as Half-day (2nd half rule).
//     if (punchInMin > SHIFT.lateHalfDayAfter) {
//         if (workedHours >= 4.5) {
//             return {
//                 status: "Half Day",
//                 reason: "Punch-in after 11:00 AM is considered Half Day.",
//                 workedHours,
//                 halfType: "2nd Half (late punch-in)",
//             };
//         }
//         return {
//             status: "Absent",
//             reason:
//                 "Punch-in after 11:00 AM needs at least 4.5 hours to be counted as Half Day; otherwise Absent.",
//             workedHours,
//         };
//     }

//     // Normal punch-in (<= 11:00 AM)
//     if (workedHours >= SHIFT.fullDayHours) {
//         return {
//             status: "Full Day Present",
//             reason: `Worked ${formatHours(workedHours)} hrs (>= 8.5 hrs).`,
//             workedHours,
//         };
//     }

//     // If >= 4 but < 8.5 => Half Day
//     // Decide half type by where the attendance mostly falls
//     let halfType = "Half Day";
//     if (punchOutMin <= SHIFT.firstHalfEnd) halfType = "1st Half (morning)";
//     else if (punchInMin >= SHIFT.secondHalfStart) halfType = "2nd Half (evening)";

//     // If it looks like 2nd half, prefer 4.5 hrs threshold
//     if (halfType.startsWith("2nd Half") && workedHours < 4.5) {
//         return {
//             status: "Absent",
//             reason: "2nd Half requires at least 4.5 hours; otherwise Absent.",
//             workedHours,
//         };
//     }

//     return {
//         status: "Half Day",
//         reason: `Worked ${formatHours(workedHours)} hrs (< 8.5 hrs).`,
//         workedHours,
//         halfType,
//     };
// }

// export default function AttendancePage() {
//     const [punchIn, setPunchIn] = useState("");
//     const [punchOut, setPunchOut] = useState("");
//     const [result, setResult] = useState(null);

//     const punchInMin = useMemo(() => parseTimeToMinutes(punchIn), [punchIn]);
//     const punchOutMin = useMemo(() => parseTimeToMinutes(punchOut), [punchOut]);

//     const onSubmit = (e) => {
//         e.preventDefault();
//         const res = getStatus(punchInMin, punchOutMin);
//         setResult(res);
//     };

//     const badgeStyle = (status) => {
//         const base = {
//             padding: "8px 12px",
//             borderRadius: 999,
//             fontWeight: 700,
//             display: "inline-block",
//             fontSize: 14,
//         };
//         if (status === "Full Day Present")
//             return { ...base, background: "#E8FFF2", color: "#0E7A3A", border: "1px solid #7BE3A6" };
//         if (status === "Half Day")
//             return { ...base, background: "#FFF7E6", color: "#8A5A00", border: "1px solid #FFD18A" };
//         if (status === "Absent")
//             return { ...base, background: "#FFECEC", color: "#B42318", border: "1px solid #FFB3B3" };
//         return { ...base, background: "#F2F4F7", color: "#344054", border: "1px solid #EAECF0" };
//     };

//     return (
//         <div style={{ maxWidth: 720, margin: "30px auto", padding: 16 }}>
//             <h2 style={{ marginBottom: 6 }}>Attendance Status</h2>
//             <p style={{ marginTop: 0, color: "#475467" }}>
//                 Office Window: <b>09:00 AM – 07:30 PM</b> • Full Day: <b>8.5 hrs</b>
//             </p>

//             <form
//                 onSubmit={onSubmit}
//                 style={{
//                     background: "#fff",
//                     border: "1px solid #EAECF0",
//                     borderRadius: 12,
//                     padding: 16,
//                     boxShadow: "0 6px 18px rgba(16,24,40,0.06)",
//                 }}
//             >
//                 <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
//                     <div>
//                         <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
//                             Punch-in Time
//                         </label>
//                         <input
//                             type="time"
//                             value={punchIn}
//                             onChange={(e) => setPunchIn(e.target.value)}
//                             style={{
//                                 width: "100%",
//                                 padding: 10,
//                                 borderRadius: 10,
//                                 border: "1px solid #D0D5DD",
//                             }}
//                         />
//                         <small style={{ color: "#667085" }}>
//                             Rule: Punch-in <b>after 11:00 AM</b> ⇒ Half-day
//                         </small>
//                     </div>

//                     <div>
//                         <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
//                             Punch-out Time
//                         </label>
//                         <input
//                             type="time"
//                             value={punchOut}
//                             onChange={(e) => setPunchOut(e.target.value)}
//                             style={{
//                                 width: "100%",
//                                 padding: 10,
//                                 borderRadius: 10,
//                                 border: "1px solid #D0D5DD",
//                             }}
//                         />
//                         <small style={{ color: "#667085" }}>
//                             Rule: Worked &lt; <b>4 hrs</b> ⇒ Absent
//                         </small>
//                     </div>
//                 </div>

//                 <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
//                     <button
//                         type="submit"
//                         style={{
//                             padding: "10px 14px",
//                             borderRadius: 10,
//                             border: "none",
//                             background: "#111827",
//                             color: "#fff",
//                             fontWeight: 700,
//                             cursor: "pointer",
//                         }}
//                     >
//                         Check Status
//                     </button>

//                     <button
//                         type="button"
//                         onClick={() => {
//                             setPunchIn("");
//                             setPunchOut("");
//                             setResult(null);
//                         }}
//                         style={{
//                             padding: "10px 14px",
//                             borderRadius: 10,
//                             border: "1px solid #D0D5DD",
//                             background: "#fff",
//                             fontWeight: 700,
//                             cursor: "pointer",
//                         }}
//                     >
//                         Reset
//                     </button>
//                 </div>
//             </form>

//             {result && (
//                 <div
//                     style={{
//                         marginTop: 16,
//                         background: "#fff",
//                         border: "1px solid #EAECF0",
//                         borderRadius: 12,
//                         padding: 16,
//                     }}
//                 >
//                     <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
//                         <div>
//                             <div style={{ fontWeight: 800, marginBottom: 6 }}>Result</div>
//                             <span style={badgeStyle(result.status)}>{result.status || "—"}</span>
//                             {result.halfType && (
//                                 <div style={{ marginTop: 8, color: "#475467", fontWeight: 600 }}>
//                                     {result.halfType}
//                                 </div>
//                             )}
//                         </div>

//                         <div style={{ textAlign: "right" }}>
//                             <div style={{ fontWeight: 800, marginBottom: 6 }}>Worked Hours</div>
//                             <div style={{ fontSize: 22, fontWeight: 900 }}>
//                                 {typeof result.workedHours === "number" ? formatHours(result.workedHours) : "—"} hrs
//                             </div>
//                         </div>
//                     </div>

//                     <hr style={{ margin: "14px 0", border: "none", borderTop: "1px solid #EAECF0" }} />

//                     <div style={{ color: result.status ? "#344054" : "#B42318", fontWeight: 600 }}>
//                         {result.reason}
//                     </div>

//                     <div style={{ marginTop: 10, color: "#667085", fontSize: 13, lineHeight: 1.5 }}>
//                         <b>Rules applied:</b> Window 09:00–19:30 • Full day ≥ 8.5 hrs • Half day ≥ 4 hrs (2nd half needs ≥ 4.5)
//                         • Punch-in &gt; 11:00 ⇒ Half day • Punch-in &gt; 14:00 ⇒ Absent
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// }
